import { Form, Select } from 'antd'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'
import { useMemo } from 'react'

/** ISO 3166-1 alpha-2 code -> flag emoji (regional indicator letters). */
const flag = (iso: string): string =>
  iso
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)))

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

type PrefixOption = { value: string; label: string; search: string }

/**
 * One option per unique calling code (`+33`, `+1`, ...). Several countries can
 * share a prefix (e.g. +1); they are merged into a single option whose search
 * text keeps every country name so the picker still finds each of them.
 */
const buildOptions = (): PrefixOption[] => {
  const byPrefix = new Map<string, { iso: string; name: string }[]>()

  for (const iso of getCountries()) {
    const prefix = `+${getCountryCallingCode(iso)}`
    const name = regionNames.of(iso) ?? iso
    const list = byPrefix.get(prefix) ?? []
    list.push({ iso, name })
    byPrefix.set(prefix, list)
  }

  return [...byPrefix.entries()]
    .map(([prefix, list]) => {
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name))
      const primary = sorted[0]
      return {
        value: prefix,
        label: `${flag(primary.iso)} ${primary.name} (${prefix})`,
        search: `${sorted.map((c) => c.name).join(' ')} ${prefix}`.toLowerCase(),
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Phone-prefix criterion: a multi-select of country calling codes. Each picked
 * country contributes its prefix; the values feed `phonePrefix` (a string[]),
 * which the payload builder turns into an `or` of `phone-prefix` leaves.
 */
const PhonePrefixInput = () => {
  const options = useMemo(buildOptions, [])

  return (
    <Form.Item name="phonePrefix" noStyle>
      <Select
        mode="multiple"
        allowClear
        style={{ width: '100%' }}
        placeholder="Select one or more country prefixes"
        options={options}
        filterOption={(input, option) =>
          (option?.search ?? '').includes(input.toLowerCase())
        }
      />
    </Form.Item>
  )
}

export default PhonePrefixInput
